import React, { useId } from 'react';
import { Skeleton } from '../../BasicComponents/Skeleton';
import type { StatCardProps, TrendData } from './types';

// ─── Trend icon ───────────────────────────────────────────────────────────────

function TrendIcon({ direction }: { direction: TrendData['direction'] }) {
  if (direction === 'up') {
    return (
      <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="18 15 12 9 6 15" />
      </svg>
    );
  }
  if (direction === 'down') {
    return (
      <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    );
  }
  return (
    <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

// ─── Trend pill ───────────────────────────────────────────────────────────────

function TrendPill({ trend }: { trend: TrendData }) {
  // Determine colour: positive prop overrides direction-based default
  const isPositive =
    trend.positive !== undefined
      ? trend.positive
      : trend.direction === 'up';

  const isNeutral = trend.direction === 'flat';

  const colorClass = isNeutral
    ? 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400'
    : isPositive
      ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
      : 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400';

  // SR label: "up 12%" / "down 5%" / "flat"
  const srLabel = `${trend.direction} ${trend.value}`;

  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>
      <TrendIcon direction={trend.direction} />
      <span aria-label={srLabel}>{trend.value}</span>
    </span>
  );
}

// ─── Sparkline SVG ────────────────────────────────────────────────────────────

function Sparkline({ data }: { data: number[] }) {
  if (data.length < 2) return null;

  const W = 80;
  const H = 28;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * W;
    const y = H - ((v - min) / range) * (H - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      fill="none"
      aria-hidden="true"
      className="text-brand-500 shrink-0"
    >
      <polyline
        points={points.join(' ')}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

export function StatCard({
  title,
  value,
  icon,
  variant = 'default',
  trend,
  comparisonText,
  sparklineData,
  anomaly = false,
  loading = false,
  onClick,
}: StatCardProps) {
  const anomalyId = useId();
  const isClickable = Boolean(onClick);

  // ── Border classes ────────────────────────────────────────────────────────
  const borderClass = anomaly
    ? 'border-2 border-danger-500 animate-pulse-soft'
    : 'border border-neutral-200 dark:border-neutral-700';

  // ── Hover / focus classes (only when clickable) ───────────────────────────
  const interactiveClass = isClickable
    ? 'cursor-pointer hover:shadow-md transition-shadow duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2'
    : '';

  const cardClasses = [
    'relative flex flex-col gap-3 rounded-lg bg-white dark:bg-neutral-900 p-4 shadow-sm',
    borderClass,
    interactiveClass,
  ]
    .filter(Boolean)
    .join(' ');

  // ── Accessible label combining title + value ──────────────────────────────
  const ariaLabel = loading
    ? `${title}, loading`
    : `${title}, ${value}${anomaly ? ', anomaly detected' : ''}`;

  const sharedProps = {
    className: cardClasses,
    'aria-label': ariaLabel,
    'aria-describedby': anomaly ? anomalyId : undefined,
  };

  const content = (
    <>
      {/* Anomaly live region */}
      {anomaly && (
        <span id={anomalyId} aria-live="assertive" className="sr-only">
          Anomaly detected for {title}
        </span>
      )}

      {/* Header row: title + icon */}
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400 leading-tight">
          {title}
        </span>
        {icon && (
          <span className="flex items-center justify-center h-8 w-8 rounded-md bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 shrink-0" aria-hidden="true">
            {icon}
          </span>
        )}
      </div>

      {/* Value row */}
      {loading ? (
        <div className="flex flex-col gap-2">
          <Skeleton variant="rect" width="60%" height={28} />
          {variant !== 'default' && <Skeleton variant="text" width="40%" height={16} />}
        </div>
      ) : (
        <div className="flex items-end justify-between gap-3">
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 leading-none tabular-nums truncate">
              {value}
            </span>

            {/* Variant-specific sub-content */}
            {variant === 'trend' && trend && (
              <div className="flex items-center gap-1.5 mt-1">
                <TrendPill trend={trend} />
              </div>
            )}

            {variant === 'comparison' && comparisonText && (
              <span className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                {comparisonText}
              </span>
            )}

            {variant === 'comparison' && trend && (
              <div className="flex items-center gap-1.5 mt-1">
                <TrendPill trend={trend} />
                {comparisonText && (
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    {comparisonText}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Sparkline sits to the right of value */}
          {variant === 'sparkline' && sparklineData && sparklineData.length >= 2 && (
            <div className="flex-shrink-0 mb-1">
              <Sparkline data={sparklineData} />
            </div>
          )}
        </div>
      )}
    </>
  );

  // Render as button when clickable (spec: "rendered as a button with focus ring")
  if (isClickable) {
    return (
      <button
        type="button"
        {...sharedProps}
        onClick={onClick}
        role="article"
      >
        {content}
      </button>
    );
  }

  return (
    <div {...sharedProps} role="article">
      {content}
    </div>
  );
}
